import React from 'react';
import { Row } from '@tanstack/react-table';
import { MoreVertical } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { IamData } from '../../services/user-service';
import { useTranslation } from 'react-i18next';


/**
 * Renders the actions for a row in the IAM (Identity and Access Management) table.
 *
 * This component provides a dropdown menu with actions that can be performed on a user,
 * such as viewing details, editing the profile, resetting the password, deactivating the user,
 * or resending an activation link if the user is inactive.
 *
 * Features:
 * - Displays a dropdown with context-sensitive actions based on the user's status
 * - Allows opening an "Edit Profile" modal for user details editing
 * - Supports password reset, viewing user details, and resending activation links
 * - Prevents deactivating an already inactive user (disabled action)
 *
 * @param {DataTableRowActionsProps} props - The props for configuring the row actions
 * @param {Row<IamData>} props.row - The row data for the user, containing user information
 * @param {function} props.onViewDetails - Callback function triggered when the "View Details" action is clicked
 * @param {function} props.onResetPassword - Callback function triggered when the "Reset Password" action is clicked
 * @param {function} [props.onResendActivation] - Optional callback function triggered when the "Resend Activation" action is clicked
 *
 * @returns {JSX.Element} - The rendered row actions dropdown and the edit modal if opened
 *
 * @example
 * <DataTableRowActions
 *   row={userRow}
 *   onViewDetails={(user) => console.log('Viewing details for:', user)}
 *   onResetPassword={(user) => console.log('Resetting password for:', user)}
 *   onResendActivation={(user) => console.log('Resending activation for:', user)}
 * />
 */

interface GroupDataTableRowActionsProps {
    row: Row<IamData>;
    onDelete?: (user: IamData) => void;
    isGroupTableAction?: boolean;
    onViewDetails?: (row: IamData) => void;
    onEditGroup?: (row: IamData) => void;
}

export function GroupDataTableRowActions({
    row,
    onDelete,
    isGroupTableAction,
    onViewDetails,
    onEditGroup
}: Readonly<GroupDataTableRowActionsProps>) {
    const { t } = useTranslation();
    const user = row.original;

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete?.(user);
    };

    const handleViewDetailsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onViewDetails?.(row as any);
    };

    const onEditGroupClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onEditGroup?.(row as any);
    };


    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreVertical className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {isGroupTableAction && <DropdownMenuItem onClick={handleViewDetailsClick}>{t('VIEW_DETAILS')}</DropdownMenuItem>}
                    {isGroupTableAction && <DropdownMenuItem onClick={onEditGroupClick} >{t('RENAME_GROUP')}</DropdownMenuItem>}
                    <DropdownMenuItem className='text-error' onClick={handleEditClick}>{isGroupTableAction ? t('REMOVE_GROUP') : t('REMOVE_MEMBER')}</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}
